<template>
  <div>
    <!-- PAGE HEADER -->
    <page-header
      title="Professional Design Services"
      subtitle="Let our team of designers add branding and titles to artwork you license on Art Grab."
      bgImage="/img/headers/professional-design-services-header.webp"
    />

    <!-- USES -->
    <section class="mt-5 mb-4">
      <div class="container">
        <div class="row">
          <div class="col-12 col-md-10 offset-md-1 text-center">
            <section-header
              title="How It Works"
              subtitle="Add branding, titles, text"
              class="mb-4"
            />
            <div class="intro-text">
              Starting at $25, you can add a Professional Design Services option
              at checkout. Once you've completed payment and we recieve a few
              details about your project, a dedicated designer will get to work
              on your design.
            </div>
          </div>
          <div class="col-12 mt-5">
            <hr />
          </div>
        </div>
      </div>
    </section>
    <section>
      <div class="container-fluid mb-2">
        <div class="container">
          <div class="row">
            <div class="col-12">
              <!-- ICON CARDS -->
              <icon-card
                title="1. Purchase Your Artwork"
                iconColor="#000000"
                text="Browse the amazing collection of digital artwork in the <a href='/store'>Art Grab Shop</a>. Once you've found the perfect artwork for you, use the BUY NOW button to purchase it. During checkout, you'll have the option to select a Professional Design Services option. Choose either One Revision or Two Revisions."
              >
                <i class="fas fa-hand-holding-usd"></i>
              </icon-card>
              <icon-card
                title="2. Answer A Few Questions About Your Project"
                iconColor="#000000"
                text="After purchasing your artwork, you will recieve an email from our team requesting that you answer a few questions about your project and design preferences. We'll want to know if you have any font, color, layout preferences and if you have any logos or images for us to use."
              >
                <i class="far fa-question-circle"></i>
              </icon-card>
              <icon-card
                title="3. Our Designers Will Start Designing"
                iconColor="#000000"
                text="Once we have recieved your initial design direction, your dedicated designer will get to work. If we need additional information from you, our design team will communicate with you via email."
              >
                <i class="fas fa-pencil-ruler"></i>
              </icon-card>
              <icon-card
                title="4. Design Approval"
                iconColor="#000000"
                text="When the first pass of your design is complete, your designer will send you a preview of the design for your approval. If you love the design, that's it. If you'd like to see some changes, you can provide feedback to the designer and they will make any revisions necessary."
              >
                <i class="fas fa-eye"></i>
              </icon-card>
              <icon-card
                title="5. Design Delivery"
                iconColor="#000000"
                text="After any revisions have been made and you're happy with the completed design, your designer will send you a Google Drive link that contains the completed high-resolution design. Your design will be ready to upload to any streaming platform, print, or post on social media."
              >
                <i class="far fa-image"></i>
              </icon-card>
            </div>
          </div>
        </div>
      </div>
    </section>
    <!-- Platforms -->
    <platforms />
    <!-- FAQ CTA -->
    <call-to-action-section
      text="Ready to find the perfect piece of artwork for your next project?"
      class="bg-gradient-info"
    >
      <router-link
        to="/store"
        @click.native="resetSearch"
        class="btn btn-secondary cta-button"
        >SHOP NOW</router-link
      >
    </call-to-action-section>
    <section>
      <div class="container mt-6">
        <div class="row">
          <div class="col-12 text-center mb-3">
            <small>Example album cover designs</small>
          </div>
        </div>
        <div class="row mb-6 text-center">
          <div class="col-12 col-md-6 mb-4">
            <div class="example-card">
              <img
                src="/img/design-service/design-example-1.jpg"
                alt="Art Grab Professional Design Service retro tech album cover tokyo"
              />
            </div>
          </div>
          <div class="col-12 col-md-6 mb-4">
            <div class="example-card">
              <img
                src="/img/design-service/design-example-2.jpg"
                alt="Art Grab Professional Design Service horror metal album cover design"
              />
            </div>
          </div>
          <div class="col-12 col-md-6 mb-4">
            <div class="example-card">
              <img
                src="/img/design-service/design-example-3.jpg"
                alt="Art Grab Professional Design Service edm album cover energy"
              />
            </div>
          </div>
          <div class="col-12 col-md-6 mb-4">
            <div class="example-card">
              <img
                src="/img/design-service/design-example-4.jpg"
                alt="Art Grab Professional Design Service grunge album cover paint"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
    <section>
      <div class="container mt-6">
        <div class="row">
          <div class="col-12">
            <section-header
              title="PRICING"
              subtitle="Easy, Affordable Options"
              class="mb-4"
            />
          </div>
        </div>
        <div class="row d-flex justify-content-center">
          <div class="col-md-4 col-12">
            <card class="design-price-card">
              <i class="fas fa-dollar-sign"></i>
              <h2>One Revision</h2>
              <h3>$25.00</h3>
              <p>
                Includes one round of feedback and revision with our design
                team.
              </p>
            </card>
          </div>
          <div class="col-md-4 col-12">
            <card class="design-price-card">
              <i class="fas fa-dollar-sign"></i
              ><i class="fas fa-dollar-sign"></i>
              <h2>Two Revisions</h2>
              <h3>$40.00</h3>
              <p>
                Includes two rounds of feedback and revision with our design
                team.
              </p>
            </card>
          </div>
        </div>
      </div>
    </section>

    <section class="my-6 py-6 bg-gradient-info adobe-fonts-section">
      <div class="container-fluid">
        <div class="container">
          <div class="row">
            <div
              class="col-12 col-md-6 d-flex flex-column justify-content-center"
            >
              <div>
                <img
                  src="/img/logos/adobe-fonts-logo.png"
                  alt="Adobe Fonts logo"
                  class="adobe-fonts-logo"
                />
                <p class="mt-5">
                  Art Grab's team of designers utilizes high-quality fonts from
                  the Adobe Fonts collection. We can select the perfect font for
                  your project based on your style preferences. Or, if you'd
                  like more control, you can browse the entire Adobe Fonts
                  collection yourself and let us know which fonts you'd like to
                  use for your project.
                </p>
                <a
                  class="btn adobe-fonts-button btn-lg"
                  href="https://fonts.adobe.com/fonts"
                  target="_blank"
                  >BROWSE ADOBE FONTS</a
                >
              </div>
            </div>
            <div class="col-12 col-md-6 text-center">
              <small>These are a few of our favorite Adobe Fonts</small>
              <div>
                <img
                  src="img/design-service/adobe-fonts-favorites.png"
                  alt="Adobe Fonts favorite designer fonts"
                  class="adobe-fonts-favorites"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section>
      <div class="container mt-6">
        <div class="row">
          <div class="col-12 text-center mb-3">
            <small>Example album cover designs</small>
          </div>
        </div>
        <div class="row mb-6">
          <div class="col-12 col-md-6 mb-4">
            <div class="example-card">
              <img
                src="/img/design-service/design-example-5.jpg"
                alt="Art Grab Professional Design Service Album Cover collage eyes textured"
              />
            </div>
          </div>
          <div class="col-12 col-md-6 mb-4">
            <div class="example-card">
              <img
                src="/img/design-service/design-example-6.jpg"
                alt="Art Grab Professional Design Service Album Cover retro illustration computer"
              />
            </div>
          </div>
          <div class="col-12 col-md-6 mb-4">
            <div class="example-card">
              <img
                src="/img/design-service/design-example-7.jpg"
                alt="Art Grab Professional Design Service Album Cover neon vaporwave car"
              />
            </div>
          </div>
          <div class="col-12 col-md-6 mb-4">
            <div class="example-card">
              <img
                src="/img/design-service/design-example-8.jpg"
                alt="Art Grab Professional Design Service abstract album cover modern"
              />
            </div>
          </div>
        </div>
      </div>
    </section>

    <section>
      <div class="container">
        <div class="row">
          <div class="col-12 col-lg-8 offset-lg-2 text-center mb-6 px-5">
            <strong>
              If you licensed artwork before this service was available and
              would like to have our designers work on your project, please
              <router-link to="/contact">contact us</router-link>.
            </strong>
          </div>
        </div>
      </div>
    </section>

    <!-- FAQ CTA -->
    <call-to-action-section
      text="Ready to find the perfect piece of artwork for your next project?"
      class="bg-gradient-info"
    >
      <router-link
        to="/store"
        @click.native="resetSearch"
        class="btn btn-secondary cta-button"
        >SHOP NOW</router-link
      >
    </call-to-action-section>
  </div>
</template>

<script>
import {
  PageHeader,
  CallToActionSection,
  UsesCard,
  Platforms,
  SectionHeader,
  IconCard,
} from "../../components";
const artgrabURL = process.env.VUE_APP_ARTGRAB_URL;

export default {
  name: "professional-design-services-page",
  metaInfo() {
    return {
      title: "Art Grab : Professional Design Services",
      meta: [
        {
          vmid: "twitter:card",
          name: "twitter:card",
          content: `summary_large_image`,
        },
        {
          vmid: "twitter:title",
          name: "twitter:title",
          content: `Art Grab : Professional Design Services`,
        },
        {
          vmid: "twitter:description",
          name: "twitter:description",
          content: `Adding branding, titles, and text to your exclusively licensed artwork has never been easier.`,
        },
        {
          vmid: "twitter:image",
          name: "twitter:image",
          content:
            "https://artgrab.co/img/headers/professional-design-services-header.webp",
        },
        {
          vmid: "og:url",
          property: "og:url",
          content: `https://artgrab.co/professional-design-services`,
        },
        {
          vmid: "og:title",
          property: "og:title",
          content: `Art Grab : Professional Design Services`,
        },
        {
          vmid: "og:description",
          property: "og:description",
          content: `Adding branding, titles, and text to your exclusively licensed artwork has never been easier.`,
        },
        {
          vmid: "og:image",
          property: "og:image",
          content:
            "https://artgrab.co/img/headers/professional-design-services-header.webp",
        },
        {
          vmid: "description",
          property: "description",
          content: `Adding branding, titles, and text to your exclusively licensed artwork has never been easier.`,
        },
      ],
      link: [
        {
          rel: "canonical",
          href: `${artgrabURL}/professional-design-services`,
        },
      ],
    };
  },
  components: {
    PageHeader,
    CallToActionSection,
    Platforms,
    SectionHeader,
    IconCard,
  },
  mounted() {
    this.$store.commit("setLayout", "page-layout");
  },
  methods: {
    resetSearch() {
      this.$store.commit("setSearchPage", 1);
    },
    scrollToElement: function (element_id) {
      var bodyRect = document.body.getBoundingClientRect();
      var elemRect = document
        .getElementById(element_id)
        .getBoundingClientRect();
      var offset = elemRect.top - bodyRect.top;

      window.scrollTo({
        left: 0,
        top: offset - 90,
        behavior: "smooth",
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.design-price-card {
  text-align: center;
  border: 1px solid #e5e5e5;

  i {
    margin-top: 20px;
    margin-bottom: 20px;
    font-size: 46px;
  }

  h3 {
    font-size: 48px;
  }

  p {
    max-width: 200px;
    margin: 20px auto;
  }
}
.adobe-fonts-section {
  color: white;

  .adobe-fonts-logo {
    width: 300px;
  }

  .adobe-fonts-favorites {
    max-width: 100%;
    height: auto;
  }

  .adobe-fonts-button {
    border: 1px solid white;
    color: white;
    padding-left: 24px;
    padding-right: 24px;
    margin-top: 30px;
  }
}
.icon-cards-wrapper {
  background-color: #f7f7f7;
}

.example-card {
  img {
    max-width: 100%;
    height: auto;
    border-radius: 3px;
  }
}
.btn-secondary.cta-button {
  color: #118cef;
}

hr {
  margin-top: 12px;
}

@media (max-width: 575.98px) {
  .adobe-fonts-section {
    .adobe-fonts-button {
      width: 100%;
      margin-bottom: 30px;
    }
  }
  .intro-text {
    padding: 0 1.5rem;
  }
}
</style>
